// Кампании — персистентные (между раундами) цели отрядов:
// capture — захват слабозащищенного города противника/нейтралов;
// siege — выбить отряд противника, блокирующий свою ключевую точку/строение;
// defense — гарнизон своего города под угрозой.
import {calcBfsDistances, getNeighborHexKeys} from "../botGrid";
import {getAllQuartersOfCity} from "../../gameState/getAllQuartersOfCity";
import {findOwnCitiesUnderThreat, findWeakEnemyCities} from "../evaluation/armyStrength";
import {checkIfRuinsToken, getControlledKeyPointHexKeys, getRuinsBoostStartRound} from "../evaluation/keyPoints";
import {KEY_POINT_TOKENS} from "../../../constants/tokens";
import {FACTIONS} from "../../../constants/factions";
import {FINAL_ROUND} from "../../../configs/game";
import {NEUTRAL_PLAYER_COLOR} from "../../../constants/players";
import {
  calcStallBlacklistUntilRound,
  checkIfGrowthWaitResetsStall,
  evaluateCampaignProgress,
  isCampaignTargetBlacklisted,
  isWaitingForMarchGrowth,
  orderCaptureTargetsByReach,
  W32_MARCH_MAX_TARGET_DISTANCE,
} from "../evaluation/logistics";
import {CITY_THREAT_LEVELS, getPinnedHolders} from "../evaluation/cityCaptureThreat";
import {getUnitTypeConfig} from "../../../configs/units";
import {orderTargetsByEnemyColorFirst} from "../evaluation/bonusMovement";
import {isUnitInsectoid} from "../../factions/insectoids/isUnitInsectoid";
import {W35_SUPPLY_BLOCKED_STALL_ROUNDS} from "../../../configs/botGarrison";

// Агрессивные фракции: во второй половине не тянутся к дальним нейтралам
const CAMPAIGN_AGGRESSIVE_FACTIONS = new Set([
  FACTIONS.INSECTOIDS, FACTIONS.RATMEN, FACTIONS.ORCS, FACTIONS.DEMONS,
]);

// Волна 32 (оборона): оборону берут только отряды в пределах 6 гексов BFS (эвристика:
// без учета остановок на пустых клетках это 1-6 ходов)
const W32_DEFENSE_CAMPAIGN_MAX_DISTANCE = 6;

export const CAMPAIGN_TYPES = {
  CAPTURE: 'capture',
  SIEGE: 'siege',
  DEFENSE: 'defense',
};

const KEY_POINT_TOKEN_VALUES = Object.values(KEY_POINT_TOKENS);

/**
 * Вражеские отряды, блокирующие свои (бота) ключевые точки и строения городов
 * (нюанс #4). Возвращает Set<hexKey> блокираторов.
 */
const findBlockersOfOwnAssets = (currentPlayer, units, citiesCache, hexes, gridRenderer) => {
  const blockers = new Set();
  const ownCities = citiesCache?.[currentPlayer.color] || [];

  const ownAssetCells = new Set();
  ownCities.forEach((city) => {
    getAllQuartersOfCity(city).forEach((hexKey) => ownAssetCells.add(hexKey));
    KEY_POINT_TOKEN_VALUES.forEach((token) => {
      (city[token] || []).forEach((hexKey) => ownAssetCells.add(hexKey));
    });
    ['farm', 'barracks'].forEach((token) => {
      (city[token] || []).forEach((hexKey) => ownAssetCells.add(hexKey));
    });
  });

  units.forEach((unit) => {
    if (unit.ownerId === currentPlayer.id) {
      return;
    }
    const blocksAsset = ownAssetCells.has(unit.hexKey)
      || getNeighborHexKeys(unit.hexKey, gridRenderer).some((adjKey) => ownAssetCells.has(adjKey));
    if (blocksAsset) {
      blockers.add(unit.hexKey);
    }
  });

  return blockers;
};

// Волна 32 (логистика): кварталы города, которому принадлежит клетка (для черного
// списка застрявшей кампании: отряд не получит сразу соседний квартал того же
// города); для руин и точек — сама клетка. Вызывается только при снятии кампании
const findCityQuarterHexKeys = (citiesCache, hexKey) => {
  for (const colorCities of Object.values(citiesCache || {})) {
    for (const city of colorCities || []) {
      const quarters = getAllQuartersOfCity(city);
      if (quarters.includes(hexKey)) {
        return quarters;
      }
    }
  }
  return [hexKey];
};

/**
 * Обновляет кампании бота на этап перемещения: валидирует существующие,
 * назначает свободным отрядам цели (defense > siege > capture) и возвращает
 * {campaignByUnit, campaignDistances} для скоринга перемещений.
 */
export const updateCampaigns = ({
                                  currentPlayer,
                                  strategy,
                                  units,
                                  citiesCache,
                                  players,
                                  hexes,
                                  gridRenderer,
                                  isAggressionReady,
                                  homeDistances,
                                  expansionRadius,
                                  round,
                                  // Лимит раундов для окна дохода руин (см. getRuinsBoostStartRound);
                                  // не передан — окно от FINAL_ROUND
                                  ruinsIncomeRoundLimit,
                                  focusCaptureTargets = null,
                                  forcedCaptureByUnit = null,
                                  isAggressiveArchetype = false,
                                  // Волна 22 (21.9b): кварталы нейтральных городов с путями влияния
                                  // вентори/просвещенных — исключены из захватных кампаний до 11 раунда
                                  excludedCaptureQuarters = null,
                                  // Волна 23 (22.3): FANATICISM — неконтролируемые руины становятся
                                  // целями захвата с любого раунда (+2 очка/раунд владельцу политики)
                                  includeRuinsCaptures = false,
                                  // Волна 23 (22.2): HEGEMONISM при доминации — дополнительные цели
                                  // захвата (ближайшие к центру города противника)
                                  extraCaptureTargets = null,
                                  // Волна 25 (23.1): оборона без свободных отрядов вправе перехватить
                                  // отряд у захватной кампании (живой тест 23: демоны у центра не
                                  // возвращались защищать город в 1 ходе от захвата)
                                  allowDefenseCampaignSteal = false,
                                  // Волна 32 (логистика): null — все подчасти legacy, прежнее поведение;
                                  // иначе подчасти по гейтам (legacy-подчасть — null/false):
                                  // {march: L3 (isPreW32MarchPlayer) — null | {
                                  //    outOfSupplyUnitIds: Set|null — свои отряды вне конверта снабжения,
                                  //    supplyReachCells: Set|null — конверт снабжения ферм},
                                  //  stall: L4 (isPreW32StallPlayer) — null | {unassignableUnitIds: Set,
                                  //    maxStallRounds, blacklistRounds,
                                  //    growthHoldUnitIds: Set|null — отряды на удержании роста на казарме},
                                  //  targetOrder: L5 (isPreW32TargetOrderPlayer) — boolean}
                                  w32Logistics = null,
                                  // Волна 32 (оборона): оценки getCityCaptureThreats от вызывающего
                                  // кода (гейт isPreW32DefensePlayer); null — прежний радиус 2
                                  cityCaptureThreats = null,
                                  // Волна 32 (оборона): отряды на казарменных выдержках (лепреконы,
                                  // эльфы-PATRIOTISM, свежий d12). Оборону берут только у своего
                                  // города; null — без ограничения (legacy)
                                  defenseHeldUnitIds = null,
                                  // Волна 33 («сильные города», M6): null у всех, кроме бонусных
                                  // ботов LARGE/GREAT с осью удара — прежнее поведение; иначе
                                  // {enemyColor — цвет ближайшего противника оси,
                                  //  isArmyPhase — первые 2/3 партии}. В армейской фазе кварталы
                                  // городов enemyColor идут первыми среди захватных целей
                                  // (стабильно поверх L5); после нее новые цели дальше радиуса
                                  // экспансии не назначаются, а существующие захватные кампании
                                  // не сбрасываются, пока город слаб для армии
                                  bonusStrongCities = null,
                                  // Волна 35 (G): отряды-гарнизоны по доктрине (Set id) — не берут
                                  // кампании, их кампании снимаются, оборона их не перехватывает;
                                  // null у legacy
                                  garrisonUnitIds = null,
                                  // Волна 35 (K): null у legacy — прежнее поведение; иначе
                                  // {maxDistance — дальность захватной кампании по BFS (новой и
                                  //    действующей, кроме форсированных целей вызывающего кода),
                                  //  isFarAllowed — большой перевес армии: дальность не ограничена,
                                  //  neighborColor — цвет ближайшего агрессивного соседа: его города
                                  //    первыми среди захватных целей (null — соседа нет),
                                  //  supplyReachCells — конверт снабжения (Set|null): застревание у края
                                  //    снабжения снимает кампанию раньше общего контроля L4 и закрывает
                                  //    отряду цели за конвертом, пока действует его черный список}
                                  w35CampaignRange = null,
                                  // Волна 37 (B1, боты «золото+»): центральные кварталы своих городов
                                  // без угрозы этого раунда, у которых на смежной казарме стоит
                                  // резервный держатель (evaluation/w37Barracks): их цели обороны при
                                  // риске следующего раунда не назначаются — город держит резерв, а
                                  // кампания уводила на квартал именно его (ближайший свободный
                                  // отряд) и он терял рост. null у legacy и остальных ботов
                                  w37ReserveCityKeys = null,
                                }) => {
  const myUnits = units.filter((unit) => unit.ownerId === currentPlayer.id);
  const myArmy = myUnits.reduce((sum, unit) => sum + unit.size, 0);
  // Волна 35 (G): гарнизон по доктрине
  const isW35Garrison = (unitId) => Boolean(garrisonUnitIds && garrisonUnitIds.has(unitId));
  // Волна 32 (логистика): подчасти кампаний — L3 марш, L4 контроль прогресса, L5 порядок целей
  const w32March = w32Logistics?.march || null;
  const w32Stall = w32Logistics?.stall || null;
  const isW32TargetOrderActive = Boolean(w32Logistics?.targetOrder);
  // Волна 32: одна BFS на цель за вызов — общая для валидации, назначения и итоговых
  // дистанций (BFS зависит только от гор, не от отрядов). Чисто вычислительное —
  // включено при любой активной подчасти, решений не меняет
  const w32TargetDistances = w32Logistics ? new Map() : null;
  const getW32TargetDistances = (targetHexKey) => {
    if (!w32TargetDistances.has(targetHexKey)) {
      w32TargetDistances.set(targetHexKey, calcBfsDistances([targetHexKey], hexes, gridRenderer));
    }
    return w32TargetDistances.get(targetHexKey);
  };
  // L4: отряды на жестком удержании (без L4 — никто)
  const isW32Unassignable = (unitId) => Boolean(w32Stall?.unassignableUnitIds?.has(unitId));
  // Отряд, способный взять не-оборонную кампанию на эту цель: слабых на захват и осаду
  // не посылаем (прежний фильтр), с L4 — удерживаемые отряды не ходят, а отряд со
  // снятой застрявшей захватной кампанией несколько раундов не возвращается к городу.
  // Без L4 совпадает с прежним фильтром размера
  const isW32EligibleForTarget = (unit, type, targetHexKey) => unit.size >= 4
    && !isW32Unassignable(unit.id)
    && (type !== CAMPAIGN_TYPES.CAPTURE || !w32Stall
      || !isCampaignTargetBlacklisted(strategy.campaignStalls, unit.id, targetHexKey, round));

  const isWithinExpansion = (hexKey) => {
    if (!homeDistances) return true;
    const distance = homeDistances.get(hexKey);
    return distance !== undefined && distance <= expansionRadius;
  };

  // Волна 35 (K): дальность и застревание захватных кампаний (у legacy не вызывается).
  // BFS до цели — общая с L4/L5 (одна на цель за вызов)
  const w35TargetDistanceCache = w35CampaignRange && !w32TargetDistances ? new Map() : null;
  const getW35TargetDistances = (targetHexKey) => {
    if (w32TargetDistances) {
      return getW32TargetDistances(targetHexKey);
    }
    if (!w35TargetDistanceCache.has(targetHexKey)) {
      w35TargetDistanceCache.set(targetHexKey, calcBfsDistances([targetHexKey], hexes, gridRenderer));
    }
    return w35TargetDistanceCache.get(targetHexKey);
  };
  // Дальше двух раундов пути без большого перевеса армии (недостижимая — тоже)
  const isW35TooFar = (distance) => !w35CampaignRange.isFarAllowed
    && !(distance <= w35CampaignRange.maxDistance);
  const w35SupplyCells = w35CampaignRange ? (w35CampaignRange.supplyReachCells || null) : null;
  const w35SupplyEdgeDistanceByTarget = new Map();
  // Ближайшая к цели клетка конверта снабжения: BFS-дистанция от нее до цели
  const getW35SupplyEdgeDistance = (targetHexKey) => {
    if (!w35SupplyEdgeDistanceByTarget.has(targetHexKey)) {
      const distances = getW35TargetDistances(targetHexKey);
      let best = Infinity;
      w35SupplyCells.forEach((cellKey) => {
        const distance = distances.get(cellKey);
        if (distance !== undefined && distance < best) {
          best = distance;
        }
      });
      w35SupplyEdgeDistanceByTarget.set(targetHexKey, best);
    }
    return w35SupplyEdgeDistanceByTarget.get(targetHexKey);
  };
  // Цель за конвертом снабжения: шаг с ближайшей к ней клетки конверта уже дальше
  // от цели, чем разрешает марш L3 (−80 за клетку вне снабжения не перебить ни
  // градиентом кампании, ни захватом). Инсектоидам снабжение не нужно
  const isW35BeyondSupply = (unit, targetHexKey) => Boolean(w35SupplyCells) && !isUnitInsectoid(unit)
    && getW35SupplyEdgeDistance(targetHexKey) - 1 > W32_MARCH_MAX_TARGET_DISTANCE;
  // Отряд стоит на ближайшей к цели клетке конверта, а цель за конвертом — идти некуда
  const isW35SupplyBlocked = (unit, targetHexKey, currentDistance) => Boolean(w35SupplyCells)
    && w35SupplyCells.has(unit.hexKey)
    && isW35BeyondSupply(unit, targetHexKey)
    && currentDistance <= getW35SupplyEdgeDistance(targetHexKey);
  // После снятия застрявшей кампании отряд, пока действует его черный список, не
  // получает цели за конвертом снабжения — иначе он тут же брал соседнюю такую же
  // цель и стоял еще раунды (Сципион, 15.10: 2,-3 в R4–R6, затем -2,-1 в R7–R9)
  const isW35BarredBeyondSupply = (unit, targetHexKey) => {
    const stall = strategy.campaignStalls ? strategy.campaignStalls.get(unit.id) : null;
    return Boolean(stall && stall.untilRound >= round) && isW35BeyondSupply(unit, targetHexKey);
  };

  // Актуальные цели по типам
  const defenseTargets = new Set();
  // Волна 32 (оборона): квартал → кварталы его города и уровень риска; единственные
  // держатели кварталов угрожаемых городов (в чужие кампании не уходят)
  let defenseCityQuartersByQuarter = null;
  let defenseLevelByQuarter = null;
  let pinnedHolders = null;
  if (cityCaptureThreats) {
    defenseCityQuartersByQuarter = new Map();
    defenseLevelByQuarter = new Map();
    cityCaptureThreats.forEach((threat) => {
      if (threat.level === CITY_THREAT_LEVELS.NONE) {
        return;
      }
      // Гарнизон — только отряды НА кварталах: живучий держатель = город не захватить
      if (threat.holders.length > 0 && !threat.isHolderAtRisk) {
        return;
      }
      // Волна 37 (B1): риск следующего раунда закрывает резервный держатель на казарме
      if (w37ReserveCityKeys && threat.level === CITY_THREAT_LEVELS.NEXT_ROUND
        && w37ReserveCityKeys.has(threat.city.centralQuarter)
      ) {
        return;
      }
      // Целями — только свободные кварталы: на занятый держателем встать нельзя
      const holderCells = new Set(threat.holders.map((holder) => holder.hexKey));
      threat.quarters.forEach((quarterHexKey) => {
        defenseCityQuartersByQuarter.set(quarterHexKey, threat.quarters);
        if (holderCells.has(quarterHexKey)) {
          return;
        }
        defenseTargets.add(quarterHexKey);
        defenseLevelByQuarter.set(quarterHexKey, threat.level);
      });
    });
    pinnedHolders = getPinnedHolders(cityCaptureThreats, CITY_THREAT_LEVELS.NEXT_ROUND);
  } else {
    findOwnCitiesUnderThreat(currentPlayer, units, citiesCache, gridRenderer).forEach((threat) => {
      if (threat.garrison < threat.enemyStrengthNear) {
        threat.quarters.forEach((quarterHexKey) => defenseTargets.add(quarterHexKey));
      }
    });
  }

  // Волна 32 (оборона): отряд на казарменной выдержке сходит только на квартал
  // своего города при риске этого раунда (исключение isW32CityGuard в перемещении),
  // поэтому оборону чужого для него города он не берет и не держит: кампания
  // закрыла бы город для подвижных защитников. На казарме самого угрожаемого
  // города (смежно его кварталу) он — задуманный резерв и оборону сохраняет
  const isDefenseHeldAwayFromCity = (unit, targetHexKey) => {
    if (!defenseHeldUnitIds || !defenseCityQuartersByQuarter || !defenseHeldUnitIds.has(unit.id)) {
      return false;
    }
    const cityQuarters = defenseCityQuartersByQuarter.get(targetHexKey) || [targetHexKey];
    const unitNeighbors = getNeighborHexKeys(unit.hexKey, gridRenderer);
    return !cityQuarters.some((quarterHexKey) => quarterHexKey === unit.hexKey
      || unitNeighbors.includes(quarterHexKey));
  };

  const siegeTargets = findBlockersOfOwnAssets(currentPlayer, units, citiesCache, hexes, gridRenderer);

  const captureTargets = new Set();
  // Вторая половина игры: агрессивные фракции не берут кампании на ДАЛЬНИЕ
  // нейтральные города (живой тест 15.10) — только досягаемые за одно перемещение
  const isSecondHalfNeutralGate = round > FINAL_ROUND / 2
    && CAMPAIGN_AGGRESSIVE_FACTIONS.has(currentPlayer.factionId);
  let ownUnitDistancesForNeutrals = null;
  if (isSecondHalfNeutralGate && isAggressionReady) {
    const ownCells = units.filter((unit) => unit.ownerId === currentPlayer.id).map((unit) => unit.hexKey);
    if (ownCells.length > 0) {
      ownUnitDistancesForNeutrals = calcBfsDistances(ownCells, hexes, gridRenderer);
    }
  }
  // Волна 32 (логистика): гарнизоны кварталов слабых городов — ключ порядка целей (L5)
  // и ожидания роста у края снабжения ради марша (L3, читается только контролем
  // прогресса L4). Готовятся, когда активен хотя бы один потребитель
  const w32CaptureGarrisons = isW32TargetOrderActive || (w32March && w32Stall) ? new Map() : null;
  // Волна 33 (M6): слабые города этого вызова (для набора удержания после армейской
  // фазы) и квартал → цвет владельца (порядок целей в армейской фазе); null у остальных
  let bonusWeakCities = null;
  let bonusColorByTarget = null;
  // Волна 35 (K): квартал слабого города → цвет владельца (города ближайшего
  // агрессивного соседа — первыми); null у legacy и без соседа
  let w35ColorByTarget = null;
  if (isAggressionReady) {
    const weakCities = findWeakEnemyCities(null, currentPlayer, units, citiesCache, players, gridRenderer, myArmy);
    if (w35CampaignRange && w35CampaignRange.neighborColor) {
      w35ColorByTarget = new Map();
      weakCities.forEach((weakCity) => weakCity.quarters.forEach(
        (quarterHexKey) => w35ColorByTarget.set(quarterHexKey, weakCity.color)
      ));
    }
    if (bonusStrongCities) {
      bonusWeakCities = weakCities;
      if (bonusStrongCities.isArmyPhase) {
        bonusColorByTarget = new Map();
        weakCities.forEach((weakCity) => weakCity.quarters.forEach(
          (quarterHexKey) => bonusColorByTarget.set(quarterHexKey, weakCity.color)
        ));
      }
    }
    // Волна 20 (19.10): агрессивные архетипы приоритизируют города ЖИВЫХ
    // противников над нейтральными — нейтралы берутся кампанией, только если
    // среди слабых городов вообще нет доступного (в радиусе экспансии) города игрока
    const hasReachablePlayerWeakCity = isAggressiveArchetype && weakCities.some((weakCity) =>
      weakCity.color !== NEUTRAL_PLAYER_COLOR
      && weakCity.quarters.some((quarterHexKey) => isWithinExpansion(quarterHexKey))
    );
    weakCities
      .forEach((weakCity) => {
        if (hasReachablePlayerWeakCity && weakCity.color === NEUTRAL_PLAYER_COLOR) {
          return;
        }
        if (ownUnitDistancesForNeutrals && weakCity.color === NEUTRAL_PLAYER_COLOR
          && !weakCity.quarters.some(
            (quarterHexKey) => (ownUnitDistancesForNeutrals.get(quarterHexKey) ?? Infinity) <= 3
          )
        ) {
          return;
        }
        weakCity.quarters.forEach((quarterHexKey) => {
          if (!isWithinExpansion(quarterHexKey)) {
            return;
          }
          // Фокус роя (инсектоиды): захватные кампании только на город-фокус,
          // чтобы отряды били вместе, а не расползались по разным городам
          if (focusCaptureTargets && !focusCaptureTargets.has(quarterHexKey)) {
            return;
          }
          // Волна 22 (21.9b): нейтральный город с путем влияния — не цель кампании
          if (excludedCaptureQuarters && excludedCaptureQuarters.has(quarterHexKey)) {
            return;
          }
          captureTargets.add(quarterHexKey);
        });
      });
    if (w32CaptureGarrisons) {
      weakCities.forEach((weakCity) => weakCity.quarters.forEach(
        (quarterHexKey) => w32CaptureGarrisons.set(quarterHexKey, weakCity.garrison)
      ));
    }
  }

  // Поздняя игра: неконтролируемые руины дают удвоенный доход очков в последних
  // раундах (Руины 4, Великие 8 за раунд) — отряды идут к ним, выбивают
  // нейтральных охранников, а стройка пристраивается кварталами
  if (round >= getRuinsBoostStartRound(ruinsIncomeRoundLimit) || includeRuinsCaptures) {
    const controlledKeyPoints = getControlledKeyPointHexKeys(citiesCache);
    Object.entries(hexes).forEach(([hexKey, token]) => {
      if (checkIfRuinsToken(token) && !controlledKeyPoints.has(hexKey) && isWithinExpansion(hexKey)) {
        captureTargets.add(hexKey);
      }
    });
  }

  // Волна 23 (22.2): цели гегемонистской экспансии от вызывающего кода
  if (extraCaptureTargets) {
    extraCaptureTargets.forEach((targetHexKey) => captureTargets.add(targetHexKey));
  }

  // Волна 19 (18.11): форсированные захватные цели (d20-таран 14+ идет на
  // ближайший город живого противника) — назначаются конкретным отрядам жестко,
  // минуя гейты готовности к агрессии, и валидны, пока переданы вызывающим кодом
  if (forcedCaptureByUnit) {
    forcedCaptureByUnit.forEach((targetHexKey, unitId) => {
      captureTargets.add(targetHexKey);
      const existing = strategy.campaigns.get(unitId);
      if (!existing || existing.targetHexKey !== targetHexKey) {
        strategy.campaigns.set(unitId, {type: CAMPAIGN_TYPES.CAPTURE, targetHexKey, round});
      }
    });
  }

  // Волна 32 (логистика, L3): марш сам снижает армию (−2 размера за фазу вне снабжения),
  // и готовность к агрессии пропадает посреди марша — а с ней захватные цели и
  // разрешение марша. Отряд вне снабжения сохраняет захватную кампанию на город,
  // пока тот остается слабым для армии. Слабые города считаются один раз и только
  // если такая кампания есть
  let w32KeepCaptureTargets = null;
  const w32OutOfSupplyUnitIds = w32March?.outOfSupplyUnitIds;
  if (!isAggressionReady && w32OutOfSupplyUnitIds && w32OutOfSupplyUnitIds.size > 0
    && [...strategy.campaigns.entries()].some(([unitId, campaign]) =>
      campaign.type === CAMPAIGN_TYPES.CAPTURE && w32OutOfSupplyUnitIds.has(unitId))
  ) {
    w32KeepCaptureTargets = new Set();
    findWeakEnemyCities(null, currentPlayer, units, citiesCache, players, gridRenderer, myArmy)
      .forEach((weakCity) => weakCity.quarters.forEach((quarterHexKey) => w32KeepCaptureTargets.add(quarterHexKey)));
  }

  // Волна 33 (M6, только strongCities): после армейской фазы профиль возвращает
  // радиус экспансии и штатные пороги готовности — прежняя валидация сняла бы
  // разом все захватные кампании дальше радиуса (или вообще все при потере
  // готовности), и армия, дошедшая до соседа, развернулась бы домой. Существующая
  // захватная кампания живет, пока город слаб для армии (как удержание L3);
  // снятие — только штатным контролем прогресса L4. Новые цели по-прежнему
  // берутся из captureTargets (радиус и готовность действуют). Штатные фильтры
  // целей, кроме радиуса, действуют и здесь: квартал вне фокуса роя (инсектоиды
  // после смены фокуса), нейтрал с путем влияния (21.9b) и дальний нейтрал у
  // агрессивной фракции во второй половине (15.10) кампанию не удерживают —
  // иначе рой делился бы между прежним и новым фокусом. Слабые города считаются
  // один раз и только если такая кампания есть
  let bonusKeepCaptureTargets = null;
  if (bonusStrongCities && !bonusStrongCities.isArmyPhase
    && [...strategy.campaigns.values()].some((campaign) => campaign.type === CAMPAIGN_TYPES.CAPTURE)
  ) {
    bonusKeepCaptureTargets = new Set();
    let ownUnitDistancesForKeptNeutrals = ownUnitDistancesForNeutrals;
    if (isSecondHalfNeutralGate && !ownUnitDistancesForKeptNeutrals) {
      const ownCells = units.filter((unit) => unit.ownerId === currentPlayer.id).map((unit) => unit.hexKey);
      ownUnitDistancesForKeptNeutrals = ownCells.length > 0 ? calcBfsDistances(ownCells, hexes, gridRenderer) : null;
    }
    (bonusWeakCities || findWeakEnemyCities(null, currentPlayer, units, citiesCache, players, gridRenderer, myArmy))
      .forEach((weakCity) => {
        if (ownUnitDistancesForKeptNeutrals && weakCity.color === NEUTRAL_PLAYER_COLOR
          && !weakCity.quarters.some(
            (quarterHexKey) => (ownUnitDistancesForKeptNeutrals.get(quarterHexKey) ?? Infinity) <= 3
          )
        ) {
          return;
        }
        weakCity.quarters.forEach((quarterHexKey) => {
          if (focusCaptureTargets && !focusCaptureTargets.has(quarterHexKey)) {
            return;
          }
          if (excludedCaptureQuarters && excludedCaptureQuarters.has(quarterHexKey)) {
            return;
          }
          bonusKeepCaptureTargets.add(quarterHexKey);
        });
      });
  }

  // Волна 32 (логистика): отряд захватной кампании ждет роста — на удержании роста
  // на казарме (L4, набор от перемещения) или, при активном марше L3, у края снабжения
  // перед маршем на квартал слабого города (следующий шаг к цели вне конверта, марш
  // разрешит только больший размер — по той же модели марша, что в перемещении).
  // Вызывается только контролем прогресса L4 для живых захватных кампаний
  const isW32WaitingForGrowth = (unit, targetHexKey, targetDistances, currentDistance) => {
    if (!Number.isFinite(currentDistance) || currentDistance < 1) {
      return false;
    }
    if (w32Stall.growthHoldUnitIds && w32Stall.growthHoldUnitIds.has(unit.id)) {
      return true;
    }
    const supplyReachCells = w32March?.supplyReachCells;
    if (!supplyReachCells || !supplyReachCells.has(unit.hexKey)
      || !w32CaptureGarrisons || !w32CaptureGarrisons.has(targetHexKey)
    ) {
      return false;
    }
    return isWaitingForMarchGrowth({
      unitSize: unit.size,
      maxSize: getUnitTypeConfig(unit.type)?.maxSize || 0,
      distanceFromUnit: currentDistance,
      hasOutOfSupplyStepCloser: getNeighborHexKeys(unit.hexKey, gridRenderer).some((adjKey) =>
        targetDistances.get(adjKey) === currentDistance - 1 && !supplyReachCells.has(adjKey)),
      targetGarrison: w32CaptureGarrisons.get(targetHexKey),
      round,
    });
  };

  // Валидация существующих кампаний
  const myUnitIds = new Set(myUnits.map((unit) => unit.id));
  const myUnitById = w32Stall ? new Map(myUnits.map((unit) => [unit.id, unit])) : null;
  // Волна 32 (логистика, L4): истекшие записи черного списка и записи погибших отрядов
  if (w32Stall && strategy.campaignStalls) {
    strategy.campaignStalls.forEach((stall, unitId) => {
      if (stall.untilRound < round || !myUnitIds.has(unitId)) {
        strategy.campaignStalls.delete(unitId);
      }
    });
  }
  [...strategy.campaigns.entries()].forEach(([unitId, campaign]) => {
    if (!myUnitIds.has(unitId)) {
      strategy.campaigns.delete(unitId);
      return;
    }
    // Волна 35 (G): гарнизон стоит в своем городе — любые его кампании снимаются
    if (isW35Garrison(unitId)) {
      strategy.campaigns.delete(unitId);
      return;
    }
    const stillValid = (campaign.type === CAMPAIGN_TYPES.DEFENSE && defenseTargets.has(campaign.targetHexKey))
      || (campaign.type === CAMPAIGN_TYPES.SIEGE && siegeTargets.has(campaign.targetHexKey))
      || (campaign.type === CAMPAIGN_TYPES.CAPTURE && captureTargets.has(campaign.targetHexKey))
      // Волна 32 (логистика, L3): у legacy w32KeepCaptureTargets = null
      || (campaign.type === CAMPAIGN_TYPES.CAPTURE && w32KeepCaptureTargets !== null
        && w32OutOfSupplyUnitIds.has(unitId) && w32KeepCaptureTargets.has(campaign.targetHexKey))
      // Волна 33 (M6): после армейской фазы захватная кампания бонусного бота живет,
      // пока город слаб для армии (у остальных bonusKeepCaptureTargets = null)
      || (campaign.type === CAMPAIGN_TYPES.CAPTURE && bonusKeepCaptureTargets !== null
        && bonusKeepCaptureTargets.has(campaign.targetHexKey));
    if (!stillValid) {
      strategy.campaigns.delete(unitId);
    }
    // Волна 32 (оборона): оборона чужого города у отряда на казарменной выдержке снимается
    if (stillValid && campaign.type === CAMPAIGN_TYPES.DEFENSE
      && defenseHeldUnitIds && defenseHeldUnitIds.has(unitId)
      && isDefenseHeldAwayFromCity(myUnits.find((unit) => unit.id === unitId), campaign.targetHexKey)
    ) {
      strategy.campaigns.delete(unitId);
      return;
    }
    // Волна 32 (оборона): единственный держатель квартала угрожаемого города не уходит
    // по кампании за пределы своего города (и не занимает чужую цель захвата).
    // Его оборона своего же города тоже снимается: город он уже держит, а кампания
    // заняла бы место единственного защитника города
    const pinnedCityQuarters = pinnedHolders ? pinnedHolders.get(unitId) : null;
    if (stillValid && pinnedCityQuarters
      && (campaign.type === CAMPAIGN_TYPES.DEFENSE || !pinnedCityQuarters.includes(campaign.targetHexKey))
    ) {
      strategy.campaigns.delete(unitId);
      return;
    }
    // Волна 35 (K): захватная кампания дальше двух раундов пути без большого
    // перевеса снимается (форсированные цели вызывающий код задает сам)
    if (stillValid && w35CampaignRange && campaign.type === CAMPAIGN_TYPES.CAPTURE
      && !(forcedCaptureByUnit && forcedCaptureByUnit.has(unitId))
    ) {
      const rangeUnit = myUnits.find((unit) => unit.id === unitId);
      if (isW35TooFar(getW35TargetDistances(campaign.targetHexKey).get(rangeUnit.hexKey))) {
        strategy.campaigns.delete(unitId);
        return;
      }
    }
    // Волна 32 (логистика, L4): оборону не трогаем (блок D); форсированные цели (d20-таран,
    // зверолюды, крысолюды, штурм планировщика) каждый тик переустанавливает вызывающий код
    if (!stillValid || !w32Stall || campaign.type === CAMPAIGN_TYPES.DEFENSE
      || (forcedCaptureByUnit && forcedCaptureByUnit.has(unitId))
    ) {
      return;
    }
    // Отряд на жестком удержании (казарменные выдержки) не ходит — его захват или
    // осада держит цель, а к ней никто не идет
    if (isW32Unassignable(unitId)) {
      strategy.campaigns.delete(unitId);
      return;
    }
    if (campaign.type !== CAMPAIGN_TYPES.CAPTURE) {
      return;
    }
    const campaignUnit = myUnitById.get(unitId);
    const targetDistances = getW32TargetDistances(campaign.targetHexKey);
    const currentDistance = targetDistances.get(campaignUnit.hexKey);
    // Нарочное ожидание роста — не застревание: удержание на казарме до порога
    // роста (как в scoreUnitPosition) или стоянка у края снабжения, пока размер не
    // разрешит шаг марша за конверт. Таймер сбрасывается, только пока отряд растет
    if (isW32WaitingForGrowth(campaignUnit, campaign.targetHexKey, targetDistances, currentDistance)
      && checkIfGrowthWaitResetsStall({
        unitSize: campaignUnit.size,
        waitSize: campaign.growthWaitSize,
        waitRound: campaign.growthWaitRound,
        round,
      })
    ) {
      campaign.bestDistance = currentDistance;
      campaign.progressRound = round;
      campaign.growthWaitSize = campaignUnit.size;
      campaign.growthWaitRound = round;
      return;
    }
    const progress = evaluateCampaignProgress({
      bestDistance: campaign.bestDistance,
      progressRound: campaign.progressRound,
      currentDistance,
      round,
      maxStallRounds: w32Stall.maxStallRounds,
    });
    // Волна 35 (K): отряд на ближайшей к цели клетке конверта снабжения, а цель
    // за конвертом — кампания снимается после W35_SUPPLY_BLOCKED_STALL_ROUNDS
    // раундов без прогресса, не дожидаясь общих трех (L4)
    const isW35SupplyStalled = Boolean(w35CampaignRange) && !progress.isStalled
      && Number.isFinite(progress.progressRound)
      && round - progress.progressRound >= W35_SUPPLY_BLOCKED_STALL_ROUNDS
      && isW35SupplyBlocked(campaignUnit, campaign.targetHexKey, currentDistance);
    if (progress.isStalled || isW35SupplyStalled) {
      // Две полные фазы войны без сокращения дистанции — кампания снимается, а
      // город на несколько раундов закрыт для этого отряда
      strategy.campaigns.delete(unitId);
      if (!strategy.campaignStalls) {
        strategy.campaignStalls = new Map();
      }
      strategy.campaignStalls.set(unitId, {
        cells: findCityQuarterHexKeys(citiesCache, campaign.targetHexKey),
        untilRound: calcStallBlacklistUntilRound(round, w32Stall.blacklistRounds),
      });
      return;
    }
    campaign.bestDistance = progress.bestDistance;
    campaign.progressRound = progress.progressRound;
  });

  // Назначение свободных отрядов: приоритет defense > siege > capture, ближайшая цель
  const assignedTargets = new Set([...strategy.campaigns.values()].map((campaign) => campaign.targetHexKey));
  // Волна 32 (оборона): цель обороны закрывает весь свой город — один защитник на
  // город (осада врага на своем квартале город не закрывает)
  const markTargetAssigned = (targetHexKey, type) => {
    assignedTargets.add(targetHexKey);
    if (type === CAMPAIGN_TYPES.DEFENSE
      && defenseCityQuartersByQuarter && defenseCityQuartersByQuarter.has(targetHexKey)
    ) {
      defenseCityQuartersByQuarter.get(targetHexKey).forEach((quarterHexKey) => assignedTargets.add(quarterHexKey));
    }
  };
  if (defenseCityQuartersByQuarter) {
    strategy.campaigns.forEach((campaign) => markTargetAssigned(campaign.targetHexKey, campaign.type));
  }
  const freeUnits = myUnits.filter((unit) => !strategy.campaigns.has(unit.id)
    && !(pinnedHolders && pinnedHolders.has(unit.id))
    // Волна 35 (G): гарнизон по доктрине кампаний не берет
    && !isW35Garrison(unit.id));

  const targetGroups = [
    {type: CAMPAIGN_TYPES.DEFENSE, targets: defenseTargets},
    {type: CAMPAIGN_TYPES.SIEGE, targets: siegeTargets},
    {type: CAMPAIGN_TYPES.CAPTURE, targets: captureTargets},
  ];

  // Волна 32 (логистика, L5): незанятые кварталы СЛАБЫХ ГОРОДОВ — по пути от ближайшего
  // подходящего свободного отряда (size >= 4, не на удержании L4); гарнизон — 0.5 ячейки
  // за размер, непосильные для крупнейшего такого отряда — в конце. Руины, точки центра,
  // гегемонизм и форсированные цели — после, в прежнем порядке. Фильтры целей
  // применены раньше — меняется только порядок. Считается в момент перебора захвата,
  // после разбора обороны и осады
  const orderW32CaptureTargets = (targets) => {
    const unassignedCityTargets = [...targets].filter((hexKey) =>
      w32CaptureGarrisons.has(hexKey) && !assignedTargets.has(hexKey));
    if (unassignedCityTargets.length < 2) {
      return targets;
    }
    const capableUnits = freeUnits.filter((unit) => unit.size >= 4 && !isW32Unassignable(unit.id));
    if (capableUnits.length === 0) {
      return targets;
    }
    const maxAttackerSize = capableUnits.reduce((max, unit) => Math.max(max, unit.size), 0);
    const orderedCityTargets = orderCaptureTargetsByReach(
      unassignedCityTargets,
      calcBfsDistances(capableUnits.map((unit) => unit.hexKey), hexes, gridRenderer),
      w32CaptureGarrisons,
      maxAttackerSize,
    );
    const orderedSet = new Set(orderedCityTargets);
    return [...orderedCityTargets, ...[...targets].filter((hexKey) => !orderedSet.has(hexKey))];
  };

  targetGroups.forEach(({type, targets}) => {
    let targetsInOrder = isW32TargetOrderActive && type === CAMPAIGN_TYPES.CAPTURE
      ? orderW32CaptureTargets(targets)
      : targets;
    // Волна 33 (M6, только strongCities в армейской фазе): кварталы городов
    // ближайшего противника оси — первыми, в прежнем относительном порядке
    // (поверх L5): армия бьет соседа, а не расходится по слабым городам карты
    // Волна 35 (K): города ближайшего агрессивного соседа — первыми (поверх L5;
    // ось бонусного бота M6 ниже остается старше)
    if (w35ColorByTarget && type === CAMPAIGN_TYPES.CAPTURE) {
      targetsInOrder = orderTargetsByEnemyColorFirst(targetsInOrder, w35ColorByTarget, w35CampaignRange.neighborColor);
    }
    if (bonusColorByTarget && type === CAMPAIGN_TYPES.CAPTURE) {
      targetsInOrder = orderTargetsByEnemyColorFirst(targetsInOrder, bonusColorByTarget, bonusStrongCities.enemyColor);
    }
    targetsInOrder.forEach((targetHexKey) => {
      if (assignedTargets.has(targetHexKey)) {
        return;
      }
      // Без свободных отрядов BFS не нужен — кроме обороны с правом перехвата
      const canStealForThisTarget = allowDefenseCampaignSteal && type === CAMPAIGN_TYPES.DEFENSE
        // Волна 32 (оборона): перехват у захвата — только когда город иначе захватят уже в этом раунде
        && (!defenseLevelByQuarter || defenseLevelByQuarter.get(targetHexKey) === CITY_THREAT_LEVELS.THIS_ROUND);
      if (freeUnits.length === 0 && !canStealForThisTarget) {
        return;
      }
      // Волна 32: некого назначить — BFS цели не нужен (перехват есть только у обороны).
      // Фильтр тот же, что в переборе ниже, — решений не меняет (при любой подчасти)
      if (w32Logistics && type !== CAMPAIGN_TYPES.DEFENSE
        && !freeUnits.some((unit) => isW32EligibleForTarget(unit, type, targetHexKey))
      ) {
        return;
      }

      // Ближайший свободный отряд (слабых на осаду не посылаем)
      const targetDistances = w32TargetDistances
        ? getW32TargetDistances(targetHexKey)
        : calcBfsDistances([targetHexKey], hexes, gridRenderer);
      let bestUnitIndex = -1;
      let bestDistance = Infinity;
      freeUnits.forEach((unit, index) => {
        if (type !== CAMPAIGN_TYPES.DEFENSE && unit.size < 4) {
          return;
        }
        // Волна 32 (L4): удерживаемый отряд не берет захват или осаду; отряд со снятой
        // застрявшей кампанией несколько раундов не возвращается к тому же городу
        if (w32Stall && type !== CAMPAIGN_TYPES.DEFENSE && !isW32EligibleForTarget(unit, type, targetHexKey)) {
          return;
        }
        const distance = targetDistances.get(unit.hexKey);
        // Волна 35 (K): захват — не дальше двух раундов пути (без большого перевеса),
        // и не за конвертом снабжения у отряда, чья кампания только что застряла
        if (w35CampaignRange && type === CAMPAIGN_TYPES.CAPTURE
          && (isW35TooFar(distance) || isW35BarredBeyondSupply(unit, targetHexKey))
        ) {
          return;
        }
        // Волна 32 (оборона): дальний отряд город не спасет — не снимаем его с других задач
        if (defenseCityQuartersByQuarter && type === CAMPAIGN_TYPES.DEFENSE
          && distance > W32_DEFENSE_CAMPAIGN_MAX_DISTANCE) {
          return;
        }
        // Волна 32 (оборона): отряд на выдержке не займет оборону чужого для него города
        if (type === CAMPAIGN_TYPES.DEFENSE && isDefenseHeldAwayFromCity(unit, targetHexKey)) {
          return;
        }
        if (distance !== undefined && distance < bestDistance) {
          bestDistance = distance;
          bestUnitIndex = index;
        }
      });

      if (bestUnitIndex >= 0) {
        const [unit] = freeUnits.splice(bestUnitIndex, 1);
        strategy.campaigns.set(unit.id, w32Stall && type === CAMPAIGN_TYPES.CAPTURE
          // Волна 32 (L4): таймер прогресса стартует при назначении (без лага в раунд)
          ? {type, targetHexKey, round, bestDistance, progressRound: round}
          : {type, targetHexKey, round});
        markTargetAssigned(targetHexKey, type);
        return;
      }

      // Волна 25 (23.1): свободных отрядов нет, а город без обороны — оборона
      // перехватывает БЛИЖАЙШИЙ отряд у захватной кампании (форсированные цели
      // d20-тарана и марши не трогаем — forcedCaptureByUnit восстановит их сам,
      // как только оборона отработает и кампания освободится)
      if (canStealForThisTarget) {
        let stealUnitId = null;
        let stealDistance = Infinity;
        strategy.campaigns.forEach((campaign, unitId) => {
          if (campaign.type !== CAMPAIGN_TYPES.CAPTURE) {
            return;
          }
          if (forcedCaptureByUnit && forcedCaptureByUnit.has(unitId)) {
            return;
          }
          const unit = myUnits.find((myUnit) => myUnit.id === unitId);
          if (!unit) {
            return;
          }
          const distance = targetDistances.get(unit.hexKey);
          if (defenseCityQuartersByQuarter && distance > W32_DEFENSE_CAMPAIGN_MAX_DISTANCE) {
            return;
          }
          if (isDefenseHeldAwayFromCity(unit, targetHexKey)) {
            return;
          }
          if (distance !== undefined && distance < stealDistance) {
            stealDistance = distance;
            stealUnitId = unitId;
          }
        });
        if (stealUnitId) {
          strategy.campaigns.set(stealUnitId, {type, targetHexKey, round});
          markTargetAssigned(targetHexKey, type);
        }
      }
    });
  });

  // Дистанции до целей активных кампаний (для скоринга перемещений)
  const campaignDistances = new Map();
  strategy.campaigns.forEach((campaign) => {
    if (!campaignDistances.has(campaign.targetHexKey)) {
      campaignDistances.set(campaign.targetHexKey, w32TargetDistances
        ? getW32TargetDistances(campaign.targetHexKey)
        : calcBfsDistances([campaign.targetHexKey], hexes, gridRenderer));
    }
  });

  return {campaignByUnit: strategy.campaigns, campaignDistances};
};
